import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Recycle, Droplet, Building, Flame, ArrowRight, Award } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const PROJECTS = [
  {
    id: 'sos-agua-xxi',
    slug: 'sos-agua-xxi',
    name: 'SOS AGUA XXI',
    icon: Droplet,
    color: '#3b82f6',
    category: 'Sostenibilidad y Agricultura',
    descES: 'Desarrollo de actuaciones para impulsar el sector de la agricultura española a través de la Misión "Agricultura del siglo XXI". Sostenibilidad hídrica y eficiencia en el uso del agua.',
    descEN: 'Development of actions to boost the Spanish agriculture sector through the "Agriculture of the 21st Century" Mission. Water sustainability and efficient water use.',
    status: 'completed',
    funding: 'NextGenerationEU',
  },
  {
    id: 'ciclicom',
    slug: 'ciclicom',
    name: 'CÍCLICOM',
    icon: Recycle,
    color: '#8DC21F',
    category: 'Economía Circular',
    descES: 'Creación de un Ecosistema de Innovación para la Circularidad de Materiales Compuestos y Plásticos Complejos. Plataforma Nacional de referencia.',
    descEN: 'Creation of an Innovation Ecosystem for the Circularity of Composite Materials and Complex Plastics. National reference platform.',
    status: 'completed',
    funding: 'CDTI + EU',
    year: '2026',
  },
  {
    id: 'valdesc',
    slug: 'valdesc',
    name: 'VALDESC',
    icon: Building,
    color: '#f59e0b',
    category: 'Construcción Circular',
    descES: 'Respuesta a la necesidad de la industria de la construcción de incrementar la transformación hacia una economía circular. Valorización de residuos de construcción y demolición.',
    descEN: 'Response to the construction industry\'s need to increase transformation towards a circular economy. Recovery of construction and demolition waste.',
    status: 'active',
    funding: 'CDTI',
  },
  {
    id: 'digital-waste',
    slug: 'digital-waste',
    name: 'DIGITAL WASTE',
    icon: Recycle,
    color: '#8b5cf6',
    category: 'Digitalización',
    descES: 'Desarrollo sobre tecnologías digitales aplicadas a procesos de tratamiento de residuos. IA, IoT y gemelos digitales para optimización operativa.',
    descEN: 'Development of digital technologies applied to waste treatment processes. AI, IoT and digital twins for operational optimization.',
    status: 'active',
    funding: 'CDTI',
  },
  {
    id: 'co2mcrete',
    slug: 'co2mcrete',
    name: 'CO2MCRETE',
    icon: Flame,
    color: '#ef4444',
    category: 'Captura CO₂',
    descES: 'CO₂ Mineralizado en Cementos y Hormigones a partir de Residuos Térmicamente activados y Escorias. Reducción de huella de carbono en construcción.',
    descEN: 'CO₂ Mineralized in Cements and Concretes from Thermally activated Waste and Slags. Reduction of carbon footprint in construction.',
    status: 'completed',
    funding: 'CDTI + EU',
  },
  {
    id: 'valrec',
    slug: 'valrec',
    name: 'VALREC',
    icon: Building,
    color: '#06b6d4',
    category: 'RCD Circularidad',
    descES: 'Soluciones innovadoras para incrementar la circularidad, trazabilidad y pureza de recursos minerales presentes en los Residuos de Construcción y Demolición.',
    descEN: 'Innovative solutions to increase circularity, traceability and purity of mineral resources present in Construction and Demolition Waste.',
    status: 'completed',
    funding: 'CDTI',
  },
  {
    id: 'cents4pack',
    slug: 'cents4pack',
    name: 'CENTS4PACK',
    icon: Recycle,
    color: '#22c55e',
    category: 'Economía Circular',
    descES: 'Sistema de recompensas por el reciclaje correcto de envases. Gamificación y engagement ciudadano para aumentar tasas de reciclaje.',
    descEN: 'Reward system for correct packaging recycling. Gamification and citizen engagement to increase recycling rates.',
    status: 'completed',
    funding: 'LIFE + Partners',
  },
  {
    id: 'tecnocir',
    slug: 'tecnocir',
    name: 'TECNOCIR',
    icon: Recycle,
    color: '#a78bfa',
    category: 'Revalorización',
    descES: 'Exploración de diversas estrategias de revalorización de residuos procedentes de distintos sectores industriales. Economía circular multi-sector.',
    descEN: 'Exploration of various waste revalorization strategies from different industrial sectors. Multi-sector circular economy.',
    status: 'active',
    funding: 'CDTI',
  },
];

const STATUS_LABELS = {
  completed: { es: 'Completado', en: 'Completed', color: '#22c55e' },
  active: { es: 'Activo', en: 'Active', color: '#3b82f6' },
  pilot: { es: 'Piloto', en: 'Pilot', color: '#f59e0b' },
};

function ProjectCard({ project, index, inView, lang }) {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = project.icon;
  const desc = lang === 'es' ? project.descES : project.descEN;
  const statusLabel = STATUS_LABELS[project.status][lang] || STATUS_LABELS[project.status].en;
  const statusColor = STATUS_LABELS[project.status].color;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link
        to={`/proyectos/${project.slug}`}
        className="group block relative"
        style={{ textDecoration: 'none' }}
        data-testid={`portfolio-project-link-${project.slug}`}
      >
      <div
        className="relative h-full rounded-2xl p-6 overflow-hidden transition-all duration-500"
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.08)',
          backdropFilter: 'blur(12px)',
        }}
      >
        {/* Status badge */}
        <div 
          className="absolute top-4 right-4 z-10 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
          style={{
            background: `${statusColor}15`,
            color: statusColor,
            border: `1px solid ${statusColor}30`,
          }}
        >
          {statusLabel}
        </div>

        {/* Icon & Title */}
        <div className="flex items-start gap-4 mb-4">
          <div 
            className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
            style={{
              background: `${project.color}15`,
              border: `1px solid ${project.color}30`,
            }}
          >
            <Icon size={22} style={{ color: project.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-white mb-1 leading-tight group-hover:text-white/90 transition-colors">
              {project.name}
            </h3>
            <p className="text-xs text-white/50 uppercase tracking-wider">
              {project.category}
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-white/60 leading-relaxed mb-4">
          {desc}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award size={14} style={{ color: project.color }} />
            <span className="text-xs font-medium text-white/50">
              {project.funding}
            </span>
            {project.year && (
              <span className="text-xs text-white/30">· {project.year}</span>
            )}
          </div>
          <motion.div
            animate={{ x: isHovered ? 4 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ArrowRight size={16} style={{ color: project.color }} />
          </motion.div>
        </div>

        {/* Hover border */}
        <div 
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{ 
            border: `1px solid ${project.color}40`,
          }}
        />
      </div>
      </Link>
    </motion.div>
  );
}

export default function ProjectsPortfolioSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { lang } = useLanguage();

  return (
    <section
      ref={ref}
      className="relative py-20 sm:py-32 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #000 0%, rgba(3,8,3,0.98) 50%, #000 100%)',
      }}
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ 
          background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(141,194,31,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="v-container relative z-10">
        {/* Tag */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-4"
        >
          <span className="v-tag green">
            {lang === 'es' ? 'CARTERA DE PROYECTOS' : 'PROJECT PORTFOLIO'}
          </span>
        </motion.div>

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-[1.1] tracking-tight">
              {lang === 'es' ? (
                <>
                  Proyectos <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>financiados</em>{' '}
                  con{' '}
                  <span style={{ color: '#8DC21F' }}>impacto real</span>
                </>
              ) : (
                <>
                  <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>Funded</em> projects{' '}
                  with{' '}
                  <span style={{ color: '#8DC21F' }}>real impact</span>
                </>
              )}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <p className="text-base text-white/60 leading-relaxed mb-4">
              {lang === 'es'
                ? 'Proyectos I+D+i financiados por CDTI, NextGenerationEU y fondos europeos. Desde economía circular hasta captura de CO₂, cada proyecto impulsa la transformación del sector medioambiental.'
                : 'R&D+i projects funded by CDTI, NextGenerationEU and European funds. From circular economy to CO₂ capture, each project drives the transformation of the environmental sector.'}
            </p>
            <div className="flex items-center gap-2 text-sm text-white/50">
              <Award size={16} style={{ color: '#8DC21F' }} />
              <span>
                {lang === 'es' 
                  ? '27 proyectos I+D · ~16 M€ de cartera · Fondos EU + CDTI' 
                  : '27 R&D projects · ~€16M portfolio · EU + CDTI Funds'}
              </span>
            </div>
          </motion.div>
        </div>

        {/* Projects grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROJECTS.map((project, index) => (
            <ProjectCard
              key={project.id}
              project={project}
              index={index}
              inView={inView}
              lang={lang}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="mt-12 text-center"
        >
          <Link
            to="/proyectos"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
            data-testid="portfolio-view-all-link"
          >
            {lang === 'es' ? 'Ver todos los proyectos' : 'View all projects'}
            <ArrowRight size={14} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
